import { useId } from "react";
import "./MolecularBackground.css";

/**
 * Decorative animated molecular background.
 *
 * Variants:
 * - hero: more visible molecules for the homepage hero
 * - platform: quieter molecular graphics around section edges
 * - subtle: very light decoration for content sections
 *
 * Purely presentational:
 * - aria-hidden
 * - pointer-events none
 * - never focusable
 *
 * The parent section must use:
 * position: relative;
 * overflow: hidden;
 *
 * The parent content should use:
 * position: relative;
 * z-index: 1;
 */
export default function MolecularBackground({
  variant = "hero",
  className = "",
}) {
  // Unique per mounted instance so multiple backgrounds on one page
  // never share SVG filter or gradient IDs.
  const uid = useId().replace(/:/g, "");

  return (
    <div
      className={`molecular-background molecular-background--${variant} ${className}`}
      aria-hidden="true"
    >
      {/* Large molecule entering from the upper-left edge */}
      <Molecule
        uid={`${uid}-1`}
        className="molecule molecule--one"
        nodes={[
          [45, 75],
          [105, 38],
          [165, 72],
          [135, 135],
          [70, 145],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 0],
          [1, 3],
        ]}
        glowingNodes={[1, 3]}
      />

      {/* Central molecule */}
      <Molecule
        uid={`${uid}-2`}
        className="molecule molecule--two"
        nodes={[
          [40, 90],
          [95, 45],
          [155, 70],
          [205, 35],
          [220, 110],
          [150, 145],
          [80, 150],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 6],
          [6, 0],
          [2, 5],
        ]}
        glowingNodes={[0, 3, 5]}
      />

      {/* Lower-right molecule */}
      <Molecule
        uid={`${uid}-3`}
        className="molecule molecule--three"
        nodes={[
          [45, 55],
          [110, 30],
          [175, 65],
          [180, 130],
          [105, 155],
          [45, 120],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 0],
          [1, 4],
        ]}
        glowingNodes={[2, 4]}
      />

      {/* Smaller molecule near the lower-left/centre area */}
      <Molecule
        uid={`${uid}-4`}
        className="molecule molecule--four"
        nodes={[
          [50, 45],
          [115, 30],
          [180, 55],
          [205, 115],
          [145, 155],
          [75, 135],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 0],
          [1, 4],
          [2, 5],
        ]}
        glowingNodes={[0, 4]}
      />

      {/* Small molecule near the upper-right area */}
      <Molecule
        uid={`${uid}-5`}
        className="molecule molecule--five"
        nodes={[
          [45, 95],
          [85, 40],
          [145, 45],
          [195, 90],
          [155, 145],
          [85, 150],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 0],
          [1, 4],
        ]}
        glowingNodes={[2, 5]}
      />

      {/* Molecule partially visible along the right edge */}
      <Molecule
        uid={`${uid}-6`}
        className="molecule molecule--six"
        nodes={[
          [35, 80],
          [90, 35],
          [150, 55],
          [215, 45],
          [225, 120],
          [160, 155],
          [85, 145],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 6],
          [6, 0],
          [2, 5],
        ]}
        glowingNodes={[1, 4]}
      />

      {/* Small decorative molecule near the bottom centre */}
      <Molecule
        uid={`${uid}-7`}
        className="molecule molecule--seven"
        nodes={[
          [45, 65],
          [110, 35],
          [175, 65],
          [175, 130],
          [110, 160],
          [45, 130],
        ]}
        links={[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 0],
          [0, 3],
        ]}
        glowingNodes={[1, 3]}
      />
    </div>
  );
}

function Molecule({
  uid,
  className,
  nodes,
  links,
  glowingNodes = [],
}) {
  const glowId = `${uid}-teal-glow`;
  const gradientId = `${uid}-line-gradient`;

  return (
    <svg
      className={className}
      viewBox="0 0 260 190"
      role="presentation"
      focusable="false"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Slightly stronger but still soft brand-coloured glow */}
        <filter
          id={glowId}
          x="-120%"
          y="-120%"
          width="340%"
          height="340%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur
            stdDeviation="8"
            result="blur"
          />

          <feFlood
            floodColor="#009A97"
            floodOpacity="0.58"
            result="glowColor"
          />

          <feComposite
            in="glowColor"
            in2="blur"
            operator="in"
            result="softGlow"
          />

          <feMerge>
            <feMergeNode in="softGlow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <linearGradient
          id={gradientId}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            stopColor="#009A97"
          />
          <stop
            offset="55%"
            stopColor="#22C1A6"
          />
          <stop
            offset="100%"
            stopColor="#4D7CFE"
          />
        </linearGradient>
      </defs>

      <g className="molecule__links">
        {links.map(([startIndex, endIndex], index) => {
          const [x1, y1] = nodes[startIndex];
          const [x2, y2] = nodes[endIndex];

          return (
            <line
              key={`link-${index}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={`url(#${gradientId})`}
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          );
        })}
      </g>

      <g className="molecule__nodes">
        {nodes.map(([cx, cy], index) => {
          const isGlowing = glowingNodes.includes(index);

          return (
            <g key={`node-${index}`}>
              {isGlowing && (
                <circle
                  className="molecule__node-halo"
                  cx={cx}
                  cy={cy}
                  r="17"
                  fill="#009A97"
                  filter={`url(#${glowId})`}
                />
              )}

              <circle
                className={
                  isGlowing
                    ? "molecule__node molecule__node--glowing"
                    : "molecule__node"
                }
                cx={cx}
                cy={cy}
                r={isGlowing ? 6.8 : 4.8}
              />
            </g>
          );
        })}
      </g>
    </svg>
  );
}